/**
 * Shared helper functions for workspace operations
 */

import indicatorRegistry from "../../Registry/Indicators/IndicatorRegistry.jsx";

/**
 * Get default appearance configuration for an indicator
 * @param {string} indicatorKey - The indicator key
 * @param {string} categoryKey - The category key
 * @returns {Object} - Default appearance configuration
 */
export const getDefaultAppearance = (indicatorKey, categoryKey) => {
  try {
    if (!categoryKey) return {};

    const category = indicatorRegistry[categoryKey];
    if (!category || !category.indicators) return {};

    const registryIndicator = category.indicators[indicatorKey];
    if (!registryIndicator || !registryIndicator.defaultOptionsForAppearance)
      return {};

    const defaults = {};
    registryIndicator.defaultOptionsForAppearance.forEach((option) => {
      if (option.defaultValue) {
        defaults[option.name] = option.defaultValue;
      }
    });
    return defaults;
  } catch (error) {
    console.warn("Error getting default appearance:", error);
    return {};
  }
};

/**
 * Generate a render key from indicator key and config
 * Includes sourcePath to ensure unique keys for same indicator with different sources
 * @param {string} key - The indicator key
 * @param {Object} config - The indicator configuration
 * @returns {string} - Generated render key like "ema(10,close)"
 */
export const generateRenderKey = (key, config) => {
  if (!config || Object.keys(config).length === 0) return key;

  const params = Object.entries(config)
    .map(([k, v]) => {
      // For sourcePath, simplify nested sources (e.g., "ema(10).ema" → "ema(10)")
      // to avoid overly complex keys while still maintaining uniqueness
      if (k === "sourcePath" && typeof v === "string") {
        const dotIndex = v.indexOf(".");
        return dotIndex > -1 ? v.substring(0, dotIndex) : v;
      }
      return `${v}`;
    })
    .join(",");
  return `${key}(${params})`;
};

/**
 * Check if appearance configuration has only default values
 * @param {Object} appearanceConfig - Current appearance configuration
 * @param {string} indicatorKey - The indicator key
 * @param {string} categoryKey - The category key
 * @returns {boolean} - True if only default values
 */
export const hasOnlyDefaultAppearance = (
  appearanceConfig,
  indicatorKey,
  categoryKey,
) => {
  if (!appearanceConfig) return true;

  const defaults = getDefaultAppearance(indicatorKey, categoryKey);
  if (Object.keys(defaults).length === 0) return false; // If no defaults defined, keep all

  // Check if current appearance matches defaults
  for (const [key, defaultValue] of Object.entries(defaults)) {
    const currentValue = appearanceConfig[key];
    if (!currentValue) continue; // Property not set, could be default

    // Deep comparison for objects
    if (typeof defaultValue === "object" && typeof currentValue === "object") {
      if (JSON.stringify(defaultValue) !== JSON.stringify(currentValue)) {
        return false; // Different from default
      }
    } else if (currentValue !== defaultValue) {
      return false; // Different from default
    }
  }

  // Check for extra properties not in defaults
  for (const key of Object.keys(appearanceConfig)) {
    if (!(key in defaults)) {
      // This is an extra property, might be needed
      return false;
    }
  }

  return true; // Only default values
};
